import React, { useEffect, useState } from 'react';

import { Grid } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

import { useUpdateDeviceLeadsMutation } from '@/store/slices/api/patientsApiSlice';
import { useUpdateDeviceMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';

import { LabelAndValue } from '@/types/custom';
import { DeviceVendor, Lead } from '@/types/matchDevice';
import { DeviceLeadLocation } from '@/types/vendorTransmissionReport';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';

import LeadsEditModal from '../../DeviceLeads/LeadsEditModal/newLeads';

import styles from './DeviceEditModal.module.scss';

export const DEVICE_TYPE_OPTIONS = ['CRT-D', 'CRT-P', 'ILR', 'PM', 'ICD'].map((value) => ({
  label: value,
  value
}));

export const MANUFACTURER_OPTIONS: LabelAndValue[] = [
  { label: 'Abbott', value: 'abbott' },
  { label: 'Boston Scientific', value: 'boston_scientific' },
  { label: 'Medtronic', value: 'medtronic' },
  { label: 'Biotronik', value: 'biotronik' },
  { label: 'Sorin', value: 'sorin' }
];

export const STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' }
];

const MRI_COMPATIBILITY_OPTIONS: LabelAndValue[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const DEVICE_TELEMETRY_OPTIONS: LabelAndValue[] = [
  { label: 'Wireless', value: 'Wireless' },
  { label: 'Manual', value: 'Manual' }
];

interface DeviceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceInfo?: DeviceInfo;
  reportId?: number;
  leadsData?: Lead[] | undefined;
  isPatientView?: boolean;
}

type DeviceInfo = {
  modelName?: string;
  modelNumber?: string;
  serialNumber?: string;
  type?: string;
  implantDate?: string | null;
  vendor?: DeviceVendor;
  status?: string;
  patientId?: string;
  id?: string;
  deviceTelemetry?: string;
  isMriCompatibility?: boolean | string | null;
};

const getErrorMessage = (error: any): string => {
  if (error?.data?.message) {
    return error.data.message.trim();
  }

  if (error?.message) {
    return error.message.trim();
  }

  if (error?.status && error?.data) {
    return error.data?.message?.trim() || 'An error occurred while updating device information.';
  }

  // Fallback for any other error format
  return 'Failed to update device information.';
};

const DeviceEditModal: React.FC<DeviceEditModalProps> = ({
  isOpen,
  onClose,
  deviceInfo,
  reportId,
  leadsData: initialLeadsData,
  isPatientView = false
}) => {
  const [formDeviceInfo, setFormDeviceInfo] = useState<Partial<DeviceInfo>>({});
  const [isEditLead, setIsEditLead] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [leadsData, setLeadsData] = useState<Lead[] | undefined>(initialLeadsData);
  const [leadStatusErrors, setLeadStatusErrors] = useState<Record<string, string>>({});
  const [updateDeviceInfo] = useUpdateDeviceMutation();
  const [updateDeviceLeads] = useUpdateDeviceLeadsMutation();

  // Check if device type is ILR
  const isILRDevice = formDeviceInfo.type === 'ILR';

  useEffect(() => {
    if (isOpen && deviceInfo) {
      // Only pick fields defined in DeviceInfo type
      const allowedFields: (keyof DeviceInfo)[] = [
        'modelName',
        'modelNumber',
        'serialNumber',
        'type',
        'implantDate',
        'vendor',
        'status',
        'patientId',
        'id',
        'deviceTelemetry',
        'isMriCompatibility'
      ];
      const filteredDeviceInfo: Partial<DeviceInfo> = {};
      allowedFields.forEach((field) => {
        if (field in deviceInfo) {
          if (field === 'vendor') {
            filteredDeviceInfo.vendor = deviceInfo.vendor ?? undefined;
          } else if (field === 'isMriCompatibility') {
            // handled below
          } else {
            filteredDeviceInfo[field] = deviceInfo[field] as any;
          }
        }
      });
      filteredDeviceInfo.isMriCompatibility =
        deviceInfo.isMriCompatibility == null ? '' : deviceInfo.isMriCompatibility;
      setFormDeviceInfo(filteredDeviceInfo);
    }
  }, [isOpen, deviceInfo]);

  useEffect(() => {
    if (isOpen) {
      // Ensure at least one empty lead per chamber
      const leads = initialLeadsData ? [...initialLeadsData] : [];
      const chambers: DeviceLeadLocation[] = [
        DeviceLeadLocation.RA,
        DeviceLeadLocation.RV,
        DeviceLeadLocation.LV
      ];
      chambers.forEach((chamber) => {
        if (!leads.some((lead) => lead.leadChamber === chamber)) {
          leads.push({
            id: `tempid-${Math.random().toString(36).slice(2)}`,
            leadChamber: chamber,
            leadLocation: undefined,
            implantDate: undefined,
            serialNumber: undefined,
            leadCapped: undefined,
            leadStatus: undefined,
            deviceId: leads.length > 0 ? leads[0].deviceId : '',
            leadModel: undefined,
            leadManufacturer: { leadVendorId: null }
            // ...add any other required fields
          });
        }
      });
      setLeadsData(leads);
    }
  }, [isOpen, initialLeadsData]);

  // Handler to update a field in a lead
  const handleLeadFieldChange = (leadId: string, field: keyof Lead, value: any) => {
    setLeadsData((prev) =>
      prev?.map((lead) => (lead.id === leadId ? { ...lead, [field]: value } : lead))
    );
  };

  // Handler to add a new lead to a chamber
  const handleAddLead = (newLead: Lead) => {
    // Convert NewLead to Lead type
    const convertedLead: Lead = {
      ...newLead,
      leadManufacturer:
        newLead.leadManufacturer && typeof newLead.leadManufacturer === 'object'
          ? {
              leadVendorId:
                typeof newLead.leadManufacturer.leadVendorId === 'string'
                  ? Number(newLead.leadManufacturer.leadVendorId) || null
                  : (newLead.leadManufacturer.leadVendorId ?? null)
            }
          : undefined
    };
    setLeadsData((prev) => [...(prev || []), convertedLead]);
  };

  // Handler to delete a lead
  const handleDeleteLead = (leadId: string) => {
    setLeadsData((prev) => prev?.filter((lead) => lead.id !== leadId));
  };

  // Handler for lead status error
  const handleLeadStatusError = (leadId: string, error?: string) => {
    setLeadStatusErrors((prev) => {
      if (error) return { ...prev, [leadId]: error };
      const { [leadId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = (field: keyof DeviceInfo, value: string | DeviceVendor) => {
    setFormDeviceInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleImplantDateChange = (date: Date | null) => {
    if (date === undefined) return;
    const dateString = date === null ? '' : dayjs(date).format('MM/DD/YYYY');
    handleFieldChange('implantDate', dateString);
  };

  const handleSave = async () => {
    console.log({ leadsData });

    // For ILR devices or non-patient view, skip the leads step
    if (isPatientView && !isEditLead && !isILRDevice) {
      setIsEditLead(true);
      return;
    }

    let deviceLeadPayload;
    if (!isILRDevice) {
      // Only include leads with at least one of the required fields filled
      const filteredLeads = (leadsData ?? []).filter((lead) => {
        return (
          !!lead.implantDate ||
          (lead.leadCapped !== undefined && lead.leadCapped !== null) ||
          !!lead.leadLocation ||
          !!lead.leadModel ||
          !!lead.serialNumber ||
          !!lead.leadManufacturer?.leadVendorId
        );
      });

      // Check for missing leadStatus in leads with data
      const missingStatusLead = filteredLeads.find((lead) => !lead.leadStatus);
      if (missingStatusLead) {
        notifications.show({
          title: 'Missing Lead Status',
          message: 'Please select a Status for all leads with data.',
          color: 'red'
        });
        return;
      }

      deviceLeadPayload = filteredLeads.map((lead) => ({
        ...lead,
        id: lead.id?.includes('tempid-') ? undefined : lead.id,
        deviceId: deviceInfo?.id ?? undefined,
        leadManufacturer:
          lead.leadManufacturer && lead?.leadManufacturer?.leadVendorName !== '-'
            ? {
                ...lead.leadManufacturer,
                leadVendorId:
                  typeof lead.leadManufacturer === 'object' &&
                  'leadVendorId' in lead.leadManufacturer
                    ? (lead.leadManufacturer as any).leadVendorId
                    : lead.leadManufacturer
              }
            : { leadVendorId: null },
        implantDate: lead.implantDate ? lead.implantDate : null,
        leadLocation: lead.leadLocation ? lead.leadLocation : null
      }));
    }

    setIsSaving(true);
    const payload: DeviceInfo = {
      ...formDeviceInfo,
      implantDate: formDeviceInfo.implantDate
        ? dayjs(formDeviceInfo.implantDate).format('YYYY-MM-DDT00:00:00')
        : null,
      deviceTelemetry:
        formDeviceInfo.deviceTelemetry?.trim() === '-' ? undefined : formDeviceInfo.deviceTelemetry,
      isMriCompatibility: formDeviceInfo.isMriCompatibility
        ? formDeviceInfo.isMriCompatibility
        : deviceInfo?.isMriCompatibility
    };

    try {
      await updateDeviceInfo({
        patientId: deviceInfo?.patientId ?? '',
        ...payload,
        reportId
      } as any).unwrap();

      // Only update leads if it's not an ILR device
      if (!isILRDevice && deviceLeadPayload && isPatientView) {
        await updateDeviceLeads({
          patientId: deviceInfo?.patientId ?? '',
          deviceLeads: deviceLeadPayload,
          deviceId: deviceInfo?.id ?? '',
          reportId: reportId ?? undefined
        }).unwrap();
      }

      notifications.show({
        title: 'Success',
        message: 'Device information updated.',
        color: 'green'
      });
      setIsEditLead(false);
      onClose();
    } catch (error) {
      console.error('Device update error:', error);

      const errorMessage = getErrorMessage(error);

      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        autoClose: false // Don't auto-close for error messages so user can read them
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditLead(false);
    onClose();
  };

  const renderSelect = (
    label: string,
    options: LabelAndValue[],
    field: keyof DeviceInfo,
    value: string | undefined
  ) => {
    const selected = options.find((opt) => opt.value === value);
    return (
      <Dropdown
        label={label}
        options={options}
        value={selected}
        onChange={(option) => handleFieldChange(field, (option as LabelAndValue).value)}
        placeholder={`Select ${label}`}
        labelTextProps={{ variant: 'regular' }}
      />
    );
  };

  const renderTextInput = (label: string, field: keyof DeviceInfo) => (
    <TextInputWrapper
      label={label}
      value={
        typeof formDeviceInfo[field] === 'boolean'
          ? String(formDeviceInfo[field])
          : (formDeviceInfo[field] ?? '')
      }
      onChange={(e) => handleFieldChange(field, e.target.value)}
    />
  );

  // Determine button text based on conditions
  const getButtonText = () => {
    if (!isPatientView) return 'Save Changes';
    if (isEditLead) return 'Save Changes';
    if (isILRDevice) return 'Save Changes';
    return 'Next';
  };

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose: handleCancel,
        size: 'xl',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{ title: 'Edit Device Info' }}
      primaryButton={{
        text: getButtonText(),
        isLoading: isSaving,
        onClick: handleSave
      }}
      secondaryButton={{ text: 'Cancel', isDisabled: isSaving, onClick: handleCancel }}>
      {isEditLead ? (
        <LeadsEditModal
          leadsData={leadsData}
          onLeadFieldChange={handleLeadFieldChange}
          onAddLead={handleAddLead}
          onDeleteLead={handleDeleteLead}
          leadStatusErrors={leadStatusErrors}
          onLeadStatusError={handleLeadStatusError}
          deviceId={deviceInfo?.id}
        />
      ) : (
        <div className={styles.deviceSection}>
          <Grid mt="md" align="center">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect('Manufacturer', MANUFACTURER_OPTIONS, 'vendor', formDeviceInfo.vendor)}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect('Type', DEVICE_TYPE_OPTIONS, 'type', formDeviceInfo.type)}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderTextInput('Serial Number', 'serialNumber')}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderTextInput('Model Name', 'modelName')}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderTextInput('Model Number', 'modelNumber')}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              <DatePickerWrapper
                inputProps={{ label: 'Implant Date' }}
                datePickerProps={{
                  id: 'implantDate',
                  selected:
                    formDeviceInfo.implantDate && formDeviceInfo.implantDate !== '-'
                      ? dayjs(formDeviceInfo.implantDate).toDate()
                      : null,
                  onChange: (date: Date | null) => handleImplantDateChange(date),
                  isClearable: true
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'Device Status',
                STATUS_OPTIONS,
                'status',
                String(formDeviceInfo.status)
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'Device Telemetry',
                DEVICE_TELEMETRY_OPTIONS,
                'deviceTelemetry',
                String(formDeviceInfo.deviceTelemetry)
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'MRI Compatibility',
                MRI_COMPATIBILITY_OPTIONS,
                'isMriCompatibility',
                String(formDeviceInfo.isMriCompatibility)
              )}
            </Grid.Col>
          </Grid>
        </div>
      )}
    </CustomModal>
  );
};

export default DeviceEditModal;
